import {
  WSActions,
  WSControl,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { useEffect, useMemo, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryVerificationMissingData } from "../../../../query/onboarding/queries/useQueryVerificationMissingFields";
import {
  AcknowledgementName,
  Currency,
  InternalAccountType
} from "@wingspanhq/wingspan-api-sdk";
import {
  bankingAccountAcknowledgements,
  taxWithholdingAccountAcknowledgements
} from "../../../../shared/constants/acknowledgements";
import { Acknowledgements } from "../Acknowledgements";
import { CollectAdditionalInformation } from "../CollectAdditionalInformation";
import { useModalEConsent } from "./ModalEConsent";
import { useMutationSubmit } from "./useMutationSubmit";
import { getRequireAdditionalInformation } from "./utils";

type Props = {
  type: InternalAccountType;
  currency: Currency;
  setFundingSource?: boolean;
  setStandardPayoutDestination?: boolean;
  onBack?: () => void;
  onContinue?: () => void;
};

export const FormCreateInternalAccount: React.FC<Props> = ({
  type,
  currency,
  setFundingSource,
  setStandardPayoutDestination,
  onBack,
  onContinue
}) => {
  const queryVerificationMissingDataBanking = useQueryVerificationMissingData(
    "Banking",
    {
      refetchOnMount: "always"
    }
  );
  const queryFeatureFlags = useFeatureFlags();

  const match = useRouteMatch();

  const acknowledgements = useMemo(() => {
    switch (type) {
      case InternalAccountType.BANKING:
        return bankingAccountAcknowledgements;
      case InternalAccountType.TAX_WITHHOLDING:
        return taxWithholdingAccountAcknowledgements;
      case InternalAccountType.INTERNATIONAL_CLEARING:
        return [];
      default:
        return [];
    }
  }, [type]);

  const [showAdditionalInformation, setShowAdditionalInformation] =
    useState(false);

  const [submit, meta] = useMutationSubmit(
    type,
    currency,
    acknowledgements,
    { setFundingSource, setStandardPayoutDestination },
    {
      onSuccess: internalAccount => {
        const requireAdditionalInformation = getRequireAdditionalInformation(
          internalAccount,
          queryVerificationMissingDataBanking.data,
          queryFeatureFlags.data
        );

        if (requireAdditionalInformation) {
          setShowAdditionalInformation(true);
          return;
        }

        onContinue?.();
      }
    }
  );

  const modalEConsent = useModalEConsent();

  useEffect(() => {
    if (
      acknowledgements.includes(
        AcknowledgementName.ELECTRONIC_DISCLOSURE_AND_CONSENT
      )
    ) {
      modalEConsent.open({
        onClose: consent => {
          if (!consent) {
            onBack?.();
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acknowledgements]);

  if (showAdditionalInformation) {
    return (
      <CollectAdditionalInformation basePath={match.path} onNext={onContinue} />
    );
  }

  return (
    <WSForm
      validationSchema={Yup.object().shape({
        agreement: Yup.boolean().nullable().required("Required")
      })}
      onSubmit={submit}
    >
      <WSList gap="2XL">
        <WSPanel>
          <WSList gap="2XL">
            <WSElement>
              <WSSectionToolbar title="Activate Wingspan banking features" />
              <WSText.ParagraphSm color="gray500">
                You’ve opted to activate a Wingspan banking feature. Review and
                agree to all the agreements and terms below to continue.
              </WSText.ParagraphSm>
            </WSElement>

            <WSInfoBox>
              <p>
                By clicking continue you agree that you have read, understood,
                and agree to the following:
              </p>

              <Acknowledgements
                aknowledgements={acknowledgements.filter(
                  ack => ack !== "ElectronicDisclosureAndConsent"
                )}
              />

              <p>
                Wingspan is not a financial services technology company and is
                not a bank. Banking services provided by Lead Bank, member FDIC.
              </p>
            </WSInfoBox>

            <WSForm.Field
              name="agreement"
              component={WSControl}
              componentProps={{
                type: "checkbox",
                label:
                  "I have read, and agree to all the above-mentioned documents and their contents.",
                size: "S"
              }}
            />

            <WSErrorMessage contextKey="InternalAccount" error={meta.error} />
          </WSList>
        </WSPanel>

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Back",
              kind: "Secondary",
              onClick: onBack,
              visible: !!onBack,
              type: "button"
            },
            {
              label: "Continue",
              loading: meta.isLoading
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};
